import React from "react";
import { Server } from "lucide-react";
import { CHANNEL_COLOR, CHANNEL_LABEL, STATUS_STYLE, ago, usd } from "./networkFmt";

function Freshness({ r }) {
    const st = STATUS_STYLE[r.status] || STATUS_STYLE.not_reporting;
    return (
        <div className="flex items-center gap-1.5" title={r.status_note} data-testid={`network-server-status-${r.key}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
            <span className={`text-[11px] ${st.text}`}>
                {r.status === "not_reporting" ? r.status_note : `${r.status === "stale" ? "stale · " : ""}updated ${ago(r.reported_at)}`}
            </span>
        </div>
    );
}

function Row({ r, onSelect }) {
    return (
        <tr onClick={() => onSelect(r.key)} data-testid={`network-server-row-${r.key}`}
            className={`border-b border-white/5 cursor-pointer transition-colors duration-150 hover:bg-white/[0.04] ${r.selected ? "bg-[#7C3AED]/[0.07]" : ""}`}>
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <span className="truncate max-w-[16rem]">{r.name || r.key}</span>
                    {r.is_hub && <span className="text-[9px] uppercase tracking-wider border border-white/20 rounded px-1">hub</span>}
                    {r.is_self && <span className="text-[9px] uppercase tracking-wider border border-[#7C3AED]/50 text-[#A78BFA] rounded px-1">this server</span>}
                </div>
            </td>
            <td className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: CHANNEL_COLOR[r.channel] || CHANNEL_COLOR.other }} />
                    {CHANNEL_LABEL[r.channel] || "—"}
                </span>
            </td>
            <td className="px-4 py-2.5 text-xs uppercase text-white/60">{r.region || "—"}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-right">{usd(r.ai_cost_usd)}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-right text-white/60">{usd(r.reuse_cost_usd)}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-right text-white" data-testid={`network-server-total-${r.key}`}>{usd(r.total_cost_usd)}</td>
            <td className="px-4 py-2.5 w-36">
                <div className="flex items-center gap-2">
                    <div className="h-1 flex-1 bg-white/10 rounded"><div className="h-1 bg-[#7C3AED] rounded" style={{ width: `${Math.round(r.share * 100)}%` }} /></div>
                    <span className="font-mono text-[10px] text-white/50 w-9 text-right">{Math.round(r.share * 100)}%</span>
                </div>
            </td>
            <td className="px-4 py-2.5"><Freshness r={r} /></td>
        </tr>
    );
}

/** One row per server (whole network, same date range + client filter); click a row to select that server. */
export default function NetworkServerTable({ rows, onSelect }) {
    return (
        <div className="border border-white/10 bg-[#0a0a0a] rounded-md" data-testid="network-server-table">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <Server className="w-3.5 h-3.5 text-[#7C3AED]" /><div className="font-display text-sm">By server</div>
                <div className="text-[10px] text-white/40 ml-auto">{rows.length} server{rows.length === 1 ? "" : "s"} · click a row to select it</div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                    <thead>
                        <tr className="text-left label-xs border-b border-white/10">
                            {["Server", "Channel", "Region"].map((h) => <th key={h} className="px-4 py-2 font-normal">{h}</th>)}
                            {["AI spend", "Reuse", "Total"].map((h) => <th key={h} className="px-4 py-2 font-normal text-right">{h}</th>)}
                            <th className="px-4 py-2 font-normal">Share of network</th><th className="px-4 py-2 font-normal">Last updated</th>
                        </tr>
                    </thead>
                    <tbody>{rows.map((r) => <Row key={r.key} r={r} onSelect={onSelect} />)}</tbody>
                </table>
            </div>
        </div>
    );
}
